import { createSlice } from "@reduxjs/toolkit";

// Helper function to ensure catering cart is always in the correct format
const ensureCateringCartFormat = (cateringCart) => {
  // Always return a valid structure, regardless of input
  try {
    if (!cateringCart || typeof cateringCart !== 'object') {
      return {
        cateringCartIds: [],
        packages: [],
        items: [],
        checkoutCart: [],
        notes: "",
        filters: { date: new Date().toISOString().split('T')[0], time: "", pax: 20 },
        subtotal: 0,
        total: 0,
        itemsToRestore: null,
        isPastOrderRestoration: false,
        isModifyOrderMode: false,
        modifyOrderPax: null,
        modifyOrderId: null,
        modifyOrderOriginalTotal: null,
        pastOrderAddressId: null,
        pendingPayment: null,
      };
    }

    return {
      cateringCartIds: Array.isArray(cateringCart.cateringCartIds) ? cateringCart.cateringCartIds : [],
      packages: Array.isArray(cateringCart.packages) ? cateringCart.packages : [],
      items: Array.isArray(cateringCart.items) ? cateringCart.items : [],
      checkoutCart: Array.isArray(cateringCart.checkoutCart) ? cateringCart.checkoutCart : [],
      notes: typeof cateringCart.notes === 'string' ? cateringCart.notes : "",
      filters: cateringCart.filters && typeof cateringCart.filters === 'object' ?
        { date: new Date().toISOString().split('T')[0], time: "", pax: 20, ...cateringCart.filters } :
        { date: new Date().toISOString().split('T')[0], time: "", pax: 20 },
      subtotal: typeof cateringCart.subtotal === 'number' ? cateringCart.subtotal : 0,
      total: typeof cateringCart.total === 'number' ? cateringCart.total : 0,
      itemsToRestore: cateringCart.itemsToRestore !== undefined ? cateringCart.itemsToRestore : null,
      isPastOrderRestoration: typeof cateringCart.isPastOrderRestoration === 'boolean' ? cateringCart.isPastOrderRestoration : false,
      isModifyOrderMode: typeof cateringCart.isModifyOrderMode === 'boolean' ? cateringCart.isModifyOrderMode : false,
      modifyOrderPax: cateringCart.modifyOrderPax !== undefined ? cateringCart.modifyOrderPax : null,
      modifyOrderId: cateringCart.modifyOrderId !== undefined ? cateringCart.modifyOrderId : null,
      modifyOrderOriginalTotal: cateringCart.modifyOrderOriginalTotal !== undefined ? cateringCart.modifyOrderOriginalTotal : null,
      pastOrderAddressId: cateringCart.pastOrderAddressId !== undefined ? cateringCart.pastOrderAddressId : null,
      pendingPayment: cateringCart.pendingPayment !== undefined ? cateringCart.pendingPayment : null,
    };
  } catch (error) {
    console.error('Error in ensureCateringCartFormat:', error);
    // Return default structure on any error
    return {
      cateringCartIds: [],
      packages: [],
      items: [],
      checkoutCart: [],
      notes: "",
      filters: { date: new Date().toISOString().split('T')[0], time: "", pax: 20 },
      subtotal: 0,
      total: 0,
      itemsToRestore: null,
      isPastOrderRestoration: false,
      isModifyOrderMode: false,
      modifyOrderPax: null,
      modifyOrderId: null,
      pastOrderAddressId: null,
    };
  }
};

const initialState = {
  brandsList: null,
  cateringBrandsList: null, // Separate brands list for catering with all brands (unfiltered)
  pickupBrands: null,
  selectedBranchDetails: null,
  categories: null,
  items: null,
  cateringItems: null,
  cateringCategories: null,
  cateringPackages: null,
  cateringRestaurantId: null, // Track which restaurant the catering data belongs to
  showProductDetail: null,
  productDetail: null,
  cart: {
    items: [],
    cartIds: [],
    tax: null,
    subTotal: null,
    total: null,
    deliveryCharges: null,
    restaurantDetails: {
      restaurantId: null,
      restaurantName: null,
      branchId: null,
      branchName: null,
    },
  },
  cateringCart: ensureCateringCartFormat({
    cateringCartIds: [],
    packages: [],
    checkoutCart: [],
    notes: "",
    filters: {
      date: new Date().toISOString().split('T')[0], // Set today's date as default
      time: "",
      pax: 20
    },
    subtotal: 0,
    total: 0,
    itemsToRestore: null, // Items to restore from past orders
    isPastOrderRestoration: false, // Flag to hide Book Slot button for pending payments
    isModifyOrderMode: false, // Flag for modify order mode (PAX frozen, date/time editable)
    modifyOrderPax: null, // Original PAX for modify order mode
    modifyOrderId: null, // Order ID for modify order
    modifyOrderOriginalTotal: null, // Original total amount for modify order (must maintain or exceed)
    pastOrderAddressId: null, // Address ID to restore when navigating from past orders
    pendingPayment: null, // Track pending payment state: { status, orderInfo, timestamp }
  }),
  unavailableItems: [],
  restaurantBranches: null,
  editingVariantItem: null,
  processedReorder: false,
  processedOrderId: null,
};

const restaurantSlice = createSlice({
  name: "restaurants",
  initialState,
  reducers: {
    setBrands: (state, action) => {
      state.brandsList = action.payload;
    },
    setCateringBrands: (state, action) => {
      state.cateringBrandsList = action.payload;
    },
    setSelectedBranchDetails: (state, action) => {
      state.selectedBranchDetails = action.payload;
      state.cart.deliveryCharges = action.payload.delivery_charges

    },
    setCategories: (state, action) => {
      state.categories = action.payload;
    },
    setItems: (state, action) => {
      state.items = action.payload;
    },
    setCateringItems: (state, action) => {
      state.cateringItems = action.payload;
    },
    setCateringCategories: (state, action) => {
      state.cateringCategories = action.payload;
    },
    setCateringPackages: (state, action) => {
      state.cateringPackages = action.payload;
    },
    setCateringRestaurantId: (state, action) => {
      state.cateringRestaurantId = action.payload;
    },
    setCartItems: (state, action) => {
      const item = action.payload;
      const existingItemIndex = state.cart.items.findIndex(
        (cartItem) => cartItem.id === item.id
      );

      if (existingItemIndex !== -1) {
        // Update or remove based on quantity
        if (item.quantity <= 0) {
          state.cart.items.splice(existingItemIndex, 1); // Remove if quantity <= 0
        } else {
          state.cart.items[existingItemIndex].quantity = item.quantity; // Update quantity
        }
      } else {
        // Only add if quantity is positive
        if (item.quantity > 0 || item) {
          state.cart.items.push(item);
        }
      }
    },
    setCartVariantItems: (state, action) => {
      const { id: productId, has_variants, items: newItems } = action.payload;

      const existingProductIndex = state.cart.items.findIndex(
        (item) => item.id === productId
      );

      if (existingProductIndex === -1) {
        state.cart.items.push({
          id: productId,
          name: action.payload.name,
          price: action.payload.price,
          has_variants: has_variants,
          items: newItems,
        });
      } else {
        const existingProduct = state.cart.items[existingProductIndex];

        newItems.forEach((newItem) => {
          const existingVariantIndex = existingProduct.items.findIndex(
            (item) => item.variantId === newItem.variantId
          );

          if (existingVariantIndex !== -1) {
            existingProduct.items[existingVariantIndex].quantity += newItem.quantity;
          } else {
            existingProduct.items.push(newItem);
          }
        });

        existingProduct.items = existingProduct.items.filter(
          item => item.quantity > 0
        );
      }
    },
    removeCartItem: (state, action) => {
      state.cart.items = state.cart.items.filter(
        (item) => item.id !== action.payload.id
      );
    },
    // New: manage items nested inside packages
    addItemToCateringPackage: (state, action) => {
      const { packageId, item, packageMeta } = action.payload || {};
      if (!packageId || !item || typeof item !== 'object') return;
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      if (!Array.isArray(state.cateringCart.packages)) state.cateringCart.packages = [];
      let pkgIndex = state.cateringCart.packages.findIndex(p => p.id === packageId);
      if (pkgIndex === -1) {
        // Create a minimal package entry so items can attach
        const newPkg = { id: packageId, items: [], ...(packageMeta || {}) };
        state.cateringCart.packages.push(newPkg);
        pkgIndex = state.cateringCart.packages.length - 1;
      }
      const pkg = state.cateringCart.packages[pkgIndex];
      if (!Array.isArray(pkg.items)) pkg.items = [];
      
      // Initialize global add order counter if it doesn't exist
      if (!pkg.itemAddOrderCounter) {
        pkg.itemAddOrderCounter = 0;
      }
      
      const existingIndex = pkg.items.findIndex(cartItem => cartItem.id === item.id && !cartItem.has_variants);
      if (existingIndex !== -1) {
        const newQty = (item.quantity ?? (pkg.items[existingIndex].quantity || 0));
        const existingItem = pkg.items[existingIndex];
        
        // Check if this is a fresh restoration (item has addOrderHistory but with exact matching length)
        // vs a normal add/remove (item has addOrderHistory from existing cart item)
        const isFreshRestoration = Array.isArray(item.addOrderHistory) && 
                                   item.addOrderHistory.length === item.quantity &&
                                   (!existingItem.addOrderHistory || existingItem.addOrderHistory.length === 0);
        
        if (isFreshRestoration) {
          // Use the provided addOrderHistory directly (from order restoration)
          pkg.items[existingIndex] = { ...existingItem, ...item, quantity: newQty, addOrderHistory: item.addOrderHistory };
          
          // Update the counter to be higher than any order in the history
          const maxOrder = Math.max(...item.addOrderHistory);
          pkg.itemAddOrderCounter = Math.max(pkg.itemAddOrderCounter, maxOrder + 1);
        } else {
          // Track order of each instance when incrementing/decrementing quantity (normal flow)
          if (!Array.isArray(existingItem.addOrderHistory)) {
            // Initialize with current quantity worth of orders
            existingItem.addOrderHistory = Array.from({ length: existingItem.quantity || 0 }, (_, i) => pkg.itemAddOrderCounter++);
          }
          
          // Add new instances with incrementing order OR remove from the end
          const quantityDiff = newQty - (existingItem.quantity || 0);
          if (quantityDiff > 0) {
            for (let i = 0; i < quantityDiff; i++) {
              existingItem.addOrderHistory.push(pkg.itemAddOrderCounter++);
            }
          } else if (quantityDiff < 0) {
            // Remove from the end (most recently added)
            existingItem.addOrderHistory.splice(quantityDiff);
          }
          
          // Merge item properties but keep the updated addOrderHistory
          const updatedAddOrderHistory = existingItem.addOrderHistory;
          pkg.items[existingIndex] = { ...existingItem, ...item, quantity: newQty, addOrderHistory: updatedAddOrderHistory };
        }
        
        if (pkg.items[existingIndex].quantity <= 0) {
          pkg.items.splice(existingIndex, 1);
        }
      } else if ((item.quantity ?? 0) > 0) {
        // New item: check if addOrderHistory is provided (from order restoration)
        let addOrderHistory;
        if (Array.isArray(item.addOrderHistory) && item.addOrderHistory.length > 0 && item.addOrderHistory.length === item.quantity) {
          // Use the provided addOrderHistory (from order restoration)
          addOrderHistory = item.addOrderHistory;
          
          // Update the counter to be higher than any order in the history
          const maxOrder = Math.max(...item.addOrderHistory);
          pkg.itemAddOrderCounter = Math.max(pkg.itemAddOrderCounter, maxOrder + 1);
        } else {
          // Create addOrderHistory with order indices for each instance (normal flow)
          addOrderHistory = Array.from({ length: item.quantity || 1 }, () => pkg.itemAddOrderCounter++);
        }
        
        pkg.items.push({ ...item, addOrderHistory });
      }
    },
    removeItemFromCateringPackage: (state, action) => {
      const { packageId, itemId } = action.payload || {};
      if (!packageId || !itemId) return;
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      const pkgIndex = Array.isArray(state.cateringCart.packages)
        ? state.cateringCart.packages.findIndex(p => p.id === packageId)
        : -1;
      if (pkgIndex === -1) return;
      const pkg = state.cateringCart.packages[pkgIndex];
      if (!Array.isArray(pkg.items)) return;
      const idx = pkg.items.findIndex(it => it.id === itemId && !it.has_variants);
      if (idx !== -1) {
        const qty = Math.max(0, (pkg.items[idx].quantity || 0) - 1);
        if (qty === 0) {
          pkg.items.splice(idx, 1);
        } else {
          pkg.items[idx].quantity = qty;
        }
      }
    },
    addVariantItemToCateringPackage: (state, action) => {
      const { packageId, productId, variantId, productMeta } = action.payload || {};
      if (!packageId || !productId || !variantId) return;
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      const pkgIndex = Array.isArray(state.cateringCart.packages)
        ? state.cateringCart.packages.findIndex(p => p.id === packageId)
        : -1;
      if (pkgIndex === -1) return;
      const pkg = state.cateringCart.packages[pkgIndex];
      if (!Array.isArray(pkg.items)) pkg.items = [];
      let productIndex = pkg.items.findIndex(it => it.id === productId);
      if (productIndex === -1) {
        // create product container
        pkg.items.push({
          id: productId,
          name: productMeta?.name,
          name_en: productMeta?.name_en,
          price: productMeta?.price,
          category_id: productMeta?.category_id, // Preserve category_id for validation
          has_variants: true,
          items: [],
        });
        productIndex = pkg.items.length - 1;
      }
      const product = pkg.items[productIndex];
      if (!Array.isArray(product.items)) product.items = [];
      const variantIndex = product.items.findIndex(v => v.variantId === variantId);
      if (variantIndex !== -1) {
        product.items[variantIndex].quantity += 1;
      } else {
        product.items.push({ variantId, selectedVariant: [], quantity: 1, instructions: "" });
      }
    },
    updateVariantItemInCateringPackage: (state, action) => {
      const { packageId, productInfo, variantInfo, originalVariantId } = action.payload || {};
      if (!packageId || !productInfo?.id || !originalVariantId) return;
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      const pkgIndex = Array.isArray(state.cateringCart.packages)
        ? state.cateringCart.packages.findIndex(p => p.id === packageId)
        : -1;
      if (pkgIndex === -1) return;
      const pkg = state.cateringCart.packages[pkgIndex];
      if (!Array.isArray(pkg.items)) return;
      const productIndex = pkg.items.findIndex(it => it.id === productInfo.id);
      if (productIndex === -1) return;
      const product = pkg.items[productIndex];
      if (!Array.isArray(product.items)) return;
      const variantIndex = product.items.findIndex(item => item.variantId === originalVariantId);
      if (variantIndex !== -1) {
        product.items[variantIndex] = { ...variantInfo };
        if (variantInfo.quantity <= 0) {
          product.items.splice(variantIndex, 1);
          if (product.items.length === 0) {
            pkg.items.splice(productIndex, 1);
          }
        }
      }
    },
    removeCartVariantItems: (state, action) => {
      const { productId, variantId } = action.payload;

      const productIndex = state.cart.items.findIndex(
        (item) => item.id === productId
      );

      if (productIndex === -1) return;

      // Filter out the variant item
      const updatedItems = state.cart.items[productIndex].items.filter(
        (item) => item.variantId !== variantId
      );

      if (updatedItems.length === 0) {
        // Remove entire product if no variants left
        state.cart.items.splice(productIndex, 1);
      } else {
        // Update product's variant items
        state.cart.items[productIndex].items = updatedItems;
      }
    },
    setCartTotal: (state, action) => {
      state.cart.total = action.payload;
    },
    setCartSubTotal: (state, action) => {
      state.cart.subTotal = action.payload;
    },
    setCartTax: (state, action) => {
      state.cart.tax = action.payload;
    },
    setCartId: (state, action) => {
      state.cart.cartIds.push(action.payload.cart_id);
    },
    setCateringCartIds: (state, action) => {
      // Ensure cateringCart exists and has the correct structure
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (!Array.isArray(state.cateringCart.cateringCartIds)) {
        state.cateringCart.cateringCartIds = [];
      }

      // Ensure payload has the expected structure
      if (action.payload && typeof action.payload === 'object' && action.payload.cart_id) {
        state.cateringCart.cateringCartIds.push(action.payload.cart_id);
      }
    },
    setCartRestaurantDetails: (state, action) => {
      state.cart.restaurantDetails.restaurantName =
        action.payload.restaurantName;
      state.cart.restaurantDetails.restaurantId = action.payload.restaurant_id;
      state.cart.restaurantDetails.branchId = action.payload.branch_id;
      state.cart.restaurantDetails.branchName = action.payload.name_en;
    },
    setShowProductDetail: (state, action) => {
      state.showProductDetail = action.payload;
    },
    setProductDetail: (state, action) => {
      state.productDetail = action.payload;
    },
    addVariantItem: (state, action) => {
      const { productId, variantId } = action.payload;

      const productIndex = state.cart.items.findIndex(
        (item) => item.id === productId
      );

      if (productIndex !== -1) {
        const variantIndex = state.cart.items[productIndex].items.findIndex(
          (variant) => variant.variantId === variantId
        );

        if (variantIndex !== -1) {
          state.cart.items[productIndex].items[variantIndex].quantity += 1;
        }
      }
    },
    removeVariantItem: (state, action) => {
      const { productId, variantId } = action.payload;

      const productIndex = state.cart.items.findIndex(
        (item) => item.id === productId
      );

      if (productIndex !== -1) {
        const variantIndex = state.cart.items[productIndex].items.findIndex(
          (variant) => variant.variantId === variantId
        );

        if (variantIndex !== -1) {
          const variant = state.cart.items[productIndex].items[variantIndex];
          variant.quantity = Math.max(0, variant.quantity - 1);

          // Remove the variant if quantity is 0
          if (variant.quantity === 0) {
            state.cart.items[productIndex].items.splice(variantIndex, 1);
          }
        }

        // Remove the product if no variants are left
        if (state.cart.items[productIndex].items.length === 0) {
          state.cart.items.splice(productIndex, 1);
        }
      }
    },
    // Catering cart variant actions
    addCateringCartVariantItem: (state, action) => {
      // This reducer now requires packageId for nested structure
      const { packageId, productId, variantId } = action.payload || {};
      
      if (!packageId || !productId || !variantId) return;

      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (!Array.isArray(state.cateringCart.packages)) {
        state.cateringCart.packages = [];
      }

      const pkgIndex = state.cateringCart.packages.findIndex(p => p.id === packageId);
      if (pkgIndex === -1) return;

      const pkg = state.cateringCart.packages[pkgIndex];
      if (!Array.isArray(pkg.items)) pkg.items = [];

      const productIndex = pkg.items.findIndex(item => item.id === productId);

      if (productIndex !== -1) {
        const product = pkg.items[productIndex];
        if (!Array.isArray(product.items)) return;
        
        const variantIndex = product.items.findIndex(variant => variant.variantId === variantId);

        if (variantIndex !== -1) {
          product.items[variantIndex].quantity += 1;
        }
      }
    },
    removeCateringCartVariantItem: (state, action) => {
      // This reducer now requires packageId for nested structure
      const { packageId, productId, variantId } = action.payload || {};
      
      if (!packageId || !productId || !variantId) return;

      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (!Array.isArray(state.cateringCart.packages)) {
        state.cateringCart.packages = [];
        return;
      }

      const pkgIndex = state.cateringCart.packages.findIndex(p => p.id === packageId);
      if (pkgIndex === -1) return;

      const pkg = state.cateringCart.packages[pkgIndex];
      if (!Array.isArray(pkg.items)) return;

      const productIndex = pkg.items.findIndex(item => item.id === productId);

      if (productIndex !== -1) {
        const product = pkg.items[productIndex];
        if (!Array.isArray(product.items)) return;
        
        const variantIndex = product.items.findIndex(variant => variant.variantId === variantId);

        if (variantIndex !== -1) {
          const variant = product.items[variantIndex];
          variant.quantity = Math.max(0, variant.quantity - 1);

          // Remove the variant if quantity is 0
          if (variant.quantity === 0) {
            product.items.splice(variantIndex, 1);
            // Remove the product if no variants are left
            if (product.items.length === 0) {
              pkg.items.splice(productIndex, 1);
            }
          }
        }
      }
    },
    setUnavailableItems: (state, action) => {
      state.unavailableItems = action.payload;
    },
    clearUnavailableItems: (state) => {
      state.unavailableItems = [];
    },
    removeUnavailableItems: (state, action) => {
      const unavailableIds = action.payload;

      // Filter out completely unavailable items
      state.cart.items = state.cart.items.filter(item =>
        !unavailableIds.includes(item.id)
      );
    },
    clearCart: (state) => {
      state.cart.items = [];
      state.cart.cartIds = [];
      state.cart.tax = null;
      state.cart.subTotal = null;
      state.cart.total = null;
    },
    setPickupBrands: (state, action) => {
      state.pickupBrands = action.payload;
    },
    setRestaurantBranches: (state, action) => {
      state.restaurantBranches = action.payload;
    },
    setEditingVariantItem: (state, action) => {
      state.editingVariantItem = action.payload;
    },
    updateCartVariantItem: (state, action) => {
      const { productInfo, variantInfo, originalVariantId } = action.payload;

      // Find the product in cart
      const productIndex = state.cart.items.findIndex(item => item.id === productInfo.id);

      if (productIndex !== -1) {
        const product = state.cart.items[productIndex];

        // Find the variant item to update
        const variantIndex = product.items.findIndex(item => item.variantId === originalVariantId);

        if (variantIndex !== -1) {
          // Update the variant item
          product.items[variantIndex] = {
            ...variantInfo
          };

          // If quantity is 0, remove the variant item
          if (variantInfo.quantity <= 0) {
            product.items.splice(variantIndex, 1);

            // If no more variant items, remove the product
            if (product.items.length === 0) {
              state.cart.items.splice(productIndex, 1);
            }
          }
        }
      }
    },
    setDeliveryCharges: (state, action) => {
      state.cart.deliveryCharges = action.payload;
    },
    setProcessedReorder: (state, action) => {
      state.processedReorder = action.payload;
    },
    setProcessedOrderId: (state, action) => {
      state.processedOrderId = action.payload;
    },
    // Catering Cart reducers
    setCateringCartPackages: (state, action) => {
      // Ensure cateringCart exists and has the correct structure
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (!Array.isArray(state.cateringCart.packages)) {
        state.cateringCart.packages = [];
      }

      // Ensure payload is an array
      if (Array.isArray(action.payload)) {
        state.cateringCart.packages = action.payload;
      } else {
        state.cateringCart.packages = [];
      }
    },
    setCateringCheckoutCart: (state, action) => {
      // Ensure cateringCart exists and has the correct structure
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      if (!Array.isArray(state.cateringCart.checkoutCart)) {
        state.cateringCart.checkoutCart = [];
      }
      // Replace with array payload; if invalid, clear
      state.cateringCart.checkoutCart = Array.isArray(action.payload) ? action.payload : [];
    },
    addCateringCartPackage: (state, action) => {
      try {
        // Ensure cateringCart exists and has the correct structure
        if (!state.cateringCart || typeof state.cateringCart !== 'object') {
          state.cateringCart = ensureCateringCartFormat(state.cateringCart);
        }

        if (!Array.isArray(state.cateringCart.packages)) {
          state.cateringCart.packages = [];
        }

        // Ensure payload has the expected structure
        if (!action.payload || typeof action.payload !== 'object' || !action.payload.id) {
          console.warn('addCateringCartPackage: Invalid payload received:', action.payload);
          return;
        }

        const packageToAdd = action.payload;
        const existingPackageIndex = state.cateringCart.packages.findIndex(
          pkg => pkg.id === packageToAdd.id
        );

        if (existingPackageIndex === -1) {
          // Store the complete package detail (including pax_limit), but exclude items array from API
          const { items, ...packageWithoutItems } = packageToAdd;
          state.cateringCart.packages.push({
            ...packageWithoutItems,
            items: [] // Initialize with empty items array for cart functionality
          });
        }
      } catch (error) {
        console.error('Error in addCateringCartPackage:', error);
        // Ensure state is valid even if there's an error
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
    },
    removeCateringCartPackage: (state, action) => {
      // Ensure cateringCart exists and has the correct structure
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (!Array.isArray(state.cateringCart.packages)) {
        state.cateringCart.packages = [];
        return;
      }

      // Ensure payload is valid
      if (action.payload === undefined || action.payload === null) {
        return;
      }

      const packageId = action.payload;
      state.cateringCart.packages = state.cateringCart.packages.filter(
        pkg => pkg.id !== packageId
      );
    },
    setCateringCartFilters: (state, action) => {
      // Ensure cateringCart exists and has the correct structure
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (!state.cateringCart.filters || typeof state.cateringCart.filters !== 'object') {
        state.cateringCart.filters = { date: "", time: "", pax: 20 };
      }

      // Ensure payload is an object
      if (action.payload && typeof action.payload === 'object') {
        state.cateringCart.filters = { ...state.cateringCart.filters, ...action.payload };
      }
    },
    setCateringCartNotes: (state, action) => {
      // Ensure cateringCart exists and has the correct structure
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (typeof state.cateringCart.notes !== 'string') {
        state.cateringCart.notes = "";
      }

      // Ensure payload is a string
      if (typeof action.payload === 'string') {
        state.cateringCart.notes = action.payload;
      }
    },
    setCateringCartItems: (state, action) => {
      // This reducer now requires packageId for nested structure
      const { packageId, item, items } = action.payload || {};
      
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      
      if (!Array.isArray(state.cateringCart.packages)) {
        state.cateringCart.packages = [];
      }

      if (items && Array.isArray(items)) {
        // Replace all items in a specific package
        if (packageId) {
          const pkgIndex = state.cateringCart.packages.findIndex(p => p.id === packageId);
          if (pkgIndex !== -1) {
            state.cateringCart.packages[pkgIndex].items = items;
          }
        }
      } else if (item && typeof item === 'object' && item.id && packageId) {
        // Single item update within a specific package
        const pkgIndex = state.cateringCart.packages.findIndex(p => p.id === packageId);
        if (pkgIndex !== -1) {
          const pkg = state.cateringCart.packages[pkgIndex];
          if (!Array.isArray(pkg.items)) pkg.items = [];
          
          const existingItemIndex = pkg.items.findIndex(
            (cartItem) => cartItem.id === item.id && !cartItem.has_variants
          );

          if (existingItemIndex !== -1) {
            // Update or remove based on quantity
            if (item.quantity <= 0) {
              pkg.items.splice(existingItemIndex, 1);
            } else {
              pkg.items[existingItemIndex] = item;
            }
          } else {
            // Only add if quantity is positive
            if (item.quantity > 0) {
              pkg.items.push(item);
            }
          }
        }
      }
    },
    setCateringCartVariantItems: (state, action) => {
      // This reducer now requires packageId for nested structure
      const { packageId, id: productId, has_variants, items: newItems, name, price } = action.payload || {};
      
      if (!packageId || !productId) return;
      
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (!Array.isArray(state.cateringCart.packages)) {
        state.cateringCart.packages = [];
      }

      const pkgIndex = state.cateringCart.packages.findIndex(p => p.id === packageId);
      if (pkgIndex === -1) return;

      const pkg = state.cateringCart.packages[pkgIndex];
      if (!Array.isArray(pkg.items)) pkg.items = [];

      const existingProductIndex = pkg.items.findIndex(item => item.id === productId);

      if (existingProductIndex !== -1) {
        const existingProduct = pkg.items[existingProductIndex];

        newItems.forEach((newItem) => {
          const existingVariantIndex = existingProduct.items.findIndex(
            (item) => item.variantId === newItem.variantId
          );

          if (existingVariantIndex !== -1) {
            existingProduct.items[existingVariantIndex].quantity += newItem.quantity;
          } else {
            existingProduct.items.push(newItem);
          }
        });

        existingProduct.items = existingProduct.items.filter(
          item => item.quantity > 0
        );
      } else {
        pkg.items.push({
          id: productId,
          name: name,
          price: price,
          has_variants: has_variants,
          items: newItems,
        });
      }
    },
    updateCateringCartVariantItem: (state, action) => {
      // This reducer now requires packageId for nested structure
      const { packageId, productInfo, variantInfo, originalVariantId } = action.payload || {};
      
      if (!packageId || !productInfo?.id || !originalVariantId) return;

      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (!Array.isArray(state.cateringCart.packages)) {
        state.cateringCart.packages = [];
        return;
      }

      const pkgIndex = state.cateringCart.packages.findIndex(p => p.id === packageId);
      if (pkgIndex === -1) return;

      const pkg = state.cateringCart.packages[pkgIndex];
      if (!Array.isArray(pkg.items)) return;

      // Find the product in package
      const productIndex = pkg.items.findIndex(item => item.id === productInfo.id);

      if (productIndex !== -1) {
        const product = pkg.items[productIndex];
        if (!Array.isArray(product.items)) return;

        // Find the variant item to update
        const variantIndex = product.items.findIndex(item => item.variantId === originalVariantId);

        if (variantIndex !== -1) {
          // Update the variant item
          product.items[variantIndex] = {
            ...variantInfo
          };

          // If quantity is 0, remove the variant item
          if (variantInfo.quantity <= 0) {
            product.items.splice(variantIndex, 1);

            // If no more variant items, remove the product
            if (product.items.length === 0) {
              pkg.items.splice(productIndex, 1);
            }
          }
        }
      }
    },
    setCateringCartSubtotal: (state, action) => {
      // Ensure cateringCart exists and has the correct structure
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (typeof state.cateringCart.subtotal !== 'number') {
        state.cateringCart.subtotal = 0;
      }

      // Ensure payload is a number
      if (typeof action.payload === 'number') {
        state.cateringCart.subtotal = action.payload;
      }
    },
    setCateringCartTotal: (state, action) => {
      // Ensure cateringCart exists and has the correct structure
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      if (typeof state.cateringCart.total !== 'number') {
        state.cateringCart.total = 0;
      }

      // Ensure payload is a number
      if (typeof action.payload === 'number') {
        state.cateringCart.total = action.payload;
      }
    },
    clearCateringCart: (state) => {
      state.cateringCart = ensureCateringCartFormat({
        cateringCartIds: [],
        packages: [],
        items: [],
        checkoutCart: [],
        notes: "",
        filters: {
          date: "",
          time: "",
          pax: 20
        },
        subtotal: 0,
        total: 0,
        itemsToRestore: null,
        isPastOrderRestoration: false,
        isModifyOrderMode: false,
        modifyOrderPax: null,
        modifyOrderId: null,
        modifyOrderOriginalTotal: null,
        pastOrderAddressId: null,
      });
    },
    setCateringItemsToRestore: (state, action) => {
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      state.cateringCart.itemsToRestore = action.payload;
    },
    clearCateringItemsToRestore: (state) => {
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      state.cateringCart.itemsToRestore = null;
    },
    setIsPastOrderRestoration: (state, action) => {
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      state.cateringCart.isPastOrderRestoration = action.payload;
    },
    setModifyOrderMode: (state, action) => {
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      const { isModifyOrderMode, modifyOrderPax, modifyOrderId, modifyOrderOriginalTotal } = action.payload;
      state.cateringCart.isModifyOrderMode = isModifyOrderMode;
      state.cateringCart.modifyOrderPax = modifyOrderPax;
      state.cateringCart.modifyOrderId = modifyOrderId;
      state.cateringCart.modifyOrderOriginalTotal = modifyOrderOriginalTotal;
    },
    clearModifyOrderMode: (state) => {
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      state.cateringCart.isModifyOrderMode = false;
      state.cateringCart.modifyOrderPax = null;
      state.cateringCart.modifyOrderId = null;
      state.cateringCart.modifyOrderOriginalTotal = null;
    },
    setPastOrderAddressId: (state, action) => {
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      state.cateringCart.pastOrderAddressId = action.payload;
    },
    clearPastOrderAddressId: (state) => {
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      state.cateringCart.pastOrderAddressId = null;
    },
    setPendingPayment: (state, action) => {
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      state.cateringCart.pendingPayment = action.payload;
    },
    clearPendingPayment: (state) => {
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      state.cateringCart.pendingPayment = null;
    },
    clearAllCateringData: (state) => {
      // Clear catering cart
      state.cateringCart = ensureCateringCartFormat({
        cateringCartIds: [],
        packages: [],
        items: [],
        checkoutCart: [],
        notes: "",
        filters: {
          date: "",
          time: "",
          pax: 20
        },
        subtotal: 0,
        total: 0,
      });
      // Clear catering items, categories, packages, and brands
      state.cateringItems = null;
      state.cateringCategories = null;
      state.cateringPackages = null;
      state.cateringBrandsList = null;
      state.cateringRestaurantId = null;
    },
    replaceCateringCartWithSinglePackage: (state, action) => {
      // Clear all existing catering cart data and replace with single package
      const newPackage = action.payload;
      
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }

      // Clear all existing packages and add only the new one
      state.cateringCart.packages = newPackage ? [newPackage] : [];
      state.cateringCart.checkoutCart = [];
      state.cateringCart.notes = "";
      state.cateringCart.subtotal = 0;
      state.cateringCart.total = 0;
      
      // Keep existing filters (date, time, pax) as they should persist
    },

    removeCateringPackageCompletely: (state, action) => {
      const packageId = action.payload;
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      if (!Array.isArray(state.cateringCart.packages)) state.cateringCart.packages = [];
      state.cateringCart.packages = state.cateringCart.packages.filter(p => p.id !== packageId);
      // Also clear any legacy flat items tagged with this packageId
      if (Array.isArray(state.cateringCart.items)) {
        state.cateringCart.items = state.cateringCart.items.filter(it => it.packageId !== packageId);
      }
    },
    removeVariantItemFromCateringPackage: (state, action) => {
      const { packageId, productId, variantId } = action.payload || {};
      if (!packageId || !productId || !variantId) return;
      if (!state.cateringCart || typeof state.cateringCart !== 'object') {
        state.cateringCart = ensureCateringCartFormat(state.cateringCart);
      }
      const pkgIndex = Array.isArray(state.cateringCart.packages)
        ? state.cateringCart.packages.findIndex(p => p.id === packageId)
        : -1;
      if (pkgIndex === -1) return;
      const pkg = state.cateringCart.packages[pkgIndex];
      if (!Array.isArray(pkg.items)) return;
      const productIndex = pkg.items.findIndex(it => it.id === productId);
      if (productIndex === -1) return;
      const product = pkg.items[productIndex];
      if (!Array.isArray(product.items)) return;
      const variantIndex = product.items.findIndex(v => v.variantId === variantId);
      if (variantIndex !== -1) {
        const variant = product.items[variantIndex];
        variant.quantity = Math.max(0, variant.quantity - 1);
        if (variant.quantity === 0) {
          product.items.splice(variantIndex, 1);
          if (product.items.length === 0) {
            pkg.items.splice(productIndex, 1);
          }
        }
      }
    },
  },
});

export const {
  setBrands,
  setCateringBrands,
  setDeliveryCharges,
  setPickupBrands,
  setSelectedBranchDetails,
  setCategories,
  setItems,
  setCateringItems,
  setCateringCategories,
  setCateringPackages,
  setCateringRestaurantId,
  setCartItems,
  setCartTotal,
  setCartSubTotal,
  setCartTax,
  setCartId,
  setCartRestaurantDetails,
  setShowProductDetail,
  setProductDetail,
  removeCartItem,
  setCartVariantItems,
  removeCartVariantItems,
  addVariantItem,
  removeVariantItem,
  setUnavailableItems,
  clearUnavailableItems,
  removeUnavailableItems,
  clearCart,
  setRestaurantBranches,
  setEditingVariantItem,
  updateCartVariantItem,
  setProcessedReorder,
  setProcessedOrderId,
  setCateringCartPackages,
  addCateringCartPackage,
  removeCateringCartPackage,
  setCateringCartFilters,
  setCateringCartNotes,
  setCateringCartItems,
  setCateringCartVariantItems,
  setCateringCartSubtotal,
  setCateringCartTotal,
  clearCateringCart,
  clearAllCateringData,
  replaceCateringCartWithSinglePackage,
  addCateringCartVariantItem,
  removeCateringCartVariantItem,
  updateCateringCartVariantItem,
  setCateringCartIds,
  setCateringCheckoutCart,
  setCateringItemsToRestore,
  clearCateringItemsToRestore,
  setIsPastOrderRestoration,
  setModifyOrderMode,
  clearModifyOrderMode,
  setPastOrderAddressId,
  clearPastOrderAddressId,
  setPendingPayment,
  clearPendingPayment,
  addItemToCateringPackage,
  removeItemFromCateringPackage,
  addVariantItemToCateringPackage,
  removeVariantItemFromCateringPackage,
  updateVariantItemInCateringPackage,
  removeCateringPackageCompletely,
} = restaurantSlice.actions;
export default restaurantSlice.reducer;
